import { pdfSafe } from './pdfFont';
import { mPerLat, mPerLon } from './geo';
import { drawShapes } from './drawCanvas';
import { segLen } from './drawStyles';
import { MAINT, repairEntries } from './maint';
import JSZip from 'jszip';
import { pitRing, computePit, layerName, f2 } from './excavation';
import { I18N, ROLE_KEYS } from './i18n';
import { symbolSvg } from './fitSymbols';
import { autoAngle } from '../components/FittingExtras';
import { mainPhotoOf, fitBox, PIPE_TYPES, FITTING_TYPES, pipeLatLngs, pathLength } from './model';
import { serialize, fmtDur, extOf } from './media';
import { getAgents } from './agents';
import { APP_INFO, eur, fill } from './license';
import { tl } from './terms';
import { norm, dirText } from './photoDir';

export const clean = (s) => pdfSafe(s || '').replace(/\s+$/g, '').trim();
export const safeName = (s) => (s || 'export').replace(/[^\p{L}\p{N}_\- ]+/gu, '').trim().replace(/\s+/g, '_').slice(0, 60) || 'export';
const dateTag = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
// "Titel_Adresse_JJJJ-MM-TT"; address part uses dashes
export const fileStem = (title, address) => [safeName(title), address ? safeName(address.replace(/,/g, ' ')).replace(/_/g, '-') : '', dateTag()].filter(Boolean).join('_');
export const projectStem = (p) => fileStem(p.title, p.address || (p.mode === 'single' ? p.reports?.[0]?.address : ''));
const esc = (s) => String(s || '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

let pdfHook = null;
export const setPdfHook = (f) => { pdfHook = f; };
export function download(blob, name, noArchive = false) {
  if (pdfHook && !noArchive && /\.pdf$/i.test(name)) pdfHook(blob, name);
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1500);
}

export function loadImg(src, cors) {
  return new Promise((resolve) => {
    const img = new Image();
    if (cors) img.crossOrigin = 'anonymous';
    const timer = setTimeout(() => resolve(null), 8000);
    img.onload = () => { clearTimeout(timer); resolve(img); };
    img.onerror = () => { clearTimeout(timer); resolve(null); };
    img.src = src;
  });
}

export async function mapSnapshot(r, pipeLines = [], extras = {}, W = 720, H = 460) {
  const z = Math.min(Math.round(r.zoom || 18), 19);
  const n = 2 ** z;
  const latR = (r.lat * Math.PI) / 180;
  const cx = ((r.lon + 180) / 360) * n * 256;
  const cy = ((1 - Math.log(Math.tan(latR) + 1 / Math.cos(latR)) / Math.PI) / 2) * n * 256;
  const left = cx - W / 2;
  const top = cy - H / 2;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d');
  ctx.fillStyle = '#26323a';
  ctx.fillRect(0, 0, W, H);
  const jobs = [];
  for (let tx = Math.floor(left / 256); tx <= Math.floor((left + W) / 256); tx++) {
    for (let ty = Math.floor(top / 256); ty <= Math.floor((top + H) / 256); ty++) {
      const url = `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${z}/${ty}/${((tx % n) + n) % n}`;
      jobs.push(loadImg(url, true).then((img) => img && ctx.drawImage(img, tx * 256 - left, ty * 256 - top)));
    }
  }
  await Promise.all(jobs);
  const px = ([la, lo]) => {
    const lr = (la * Math.PI) / 180;
    return [((lo + 180) / 360) * n * 256 - left, ((1 - Math.log(Math.tan(lr) + 1 / Math.cos(lr)) / Math.PI) / 2) * n * 256 - top];
  };
  pipeLines.forEach(({ pts, color }) => {
    [['#ffffff', 8], [color, 4.5]].forEach(([c, w]) => {
      ctx.strokeStyle = c; ctx.lineWidth = w; ctx.lineJoin = 'round'; ctx.lineCap = 'round';
      ctx.beginPath();
      pts.map(px).forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
      ctx.stroke();
    });
  });
  (extras.dims || []).forEach((d) => {
    const [x1, y1] = px(d.p), [x2, y2] = px(d.q);
    ctx.save(); ctx.setLineDash([6, 4]); ctx.strokeStyle = d.base ? '#64748b' : '#0f172a'; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke(); ctx.restore();
    if (d.label) {
      ctx.font = 'bold 12px sans-serif';
      const w = ctx.measureText(d.label).width + 8, mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
      ctx.fillStyle = '#facc15'; ctx.fillRect(mx - w / 2, my - 9, w, 18);
      ctx.fillStyle = '#0f172a'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillText(d.label, mx, my); ctx.textAlign = 'start'; ctx.textBaseline = 'alphabetic';
    }
  });
  if (extras.drawings?.length) drawShapes(ctx, extras.drawings, px, (a, b) => `${segLen(a, b).toFixed(2).replace('.', ',')} m`);
  (extras.pits || []).forEach((p) => {
    ctx.beginPath();
    pitRing(p).map(px).forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    ctx.closePath(); ctx.fillStyle = 'rgba(249,115,22,0.4)'; ctx.fill();
    ctx.strokeStyle = '#c2410c'; ctx.lineWidth = 2.5; ctx.stroke();
  });
  (extras.polys || []).forEach((p) => {
    ctx.beginPath();
    p.pts.map(px).forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    ctx.closePath(); ctx.globalAlpha = p.alpha ?? 0.3; ctx.fillStyle = p.color; ctx.fill(); ctx.globalAlpha = 1;
    ctx.strokeStyle = p.stroke || p.color; ctx.lineWidth = p.w ?? 1.5; ctx.stroke();
  });
  // hydraulic symbols (valve, cross, node …) as on the map, in the chosen standard / DIN style
  const syms = extras.netPipes ? await Promise.all((extras.fittings || []).map((f) => {
    const sv = symbolSvg(f, { ang: autoAngle(f, extras.netPipes, extras.fittings), pipes: extras.netPipes, fittings: extras.fittings, img: true });
    return sv ? loadImg(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(sv)}`, false) : null;
  })) : [];
  (extras.fittings || []).forEach((f, i) => {
    const [x, y] = px([f.lat, f.lon]);
    if (syms[i]) { const w = syms[i].width || 28; ctx.drawImage(syms[i], x - w / 2, y - w / 2, w, w); return; }
    if (f.maint?.status) { ctx.beginPath(); ctx.arc(x, y, 15, 0, Math.PI * 2); ctx.lineWidth = 4; ctx.strokeStyle = MAINT[f.maint.status]; ctx.stroke(); }
    ctx.fillStyle = f.maint?.status ? MAINT[f.maint.status] : f.color || FITTING_TYPES[f.type] || FITTING_TYPES.other;
    ctx.strokeStyle = '#fff'; ctx.lineWidth = 2;
    ctx.beginPath();
    if (f.type === 'manhole') ctx.rect(x - 10, y - 10, 20, 20); else ctx.arc(x, y, 10, 0, Math.PI * 2);
    ctx.fill(); ctx.stroke();
    ctx.fillStyle = f.maint?.status === 'warn' ? '#111' : '#fff'; ctx.font = 'bold 9px Helvetica, Arial'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(f.sym || extras.t?.['fab_' + f.type] || '', x, y + 0.5);
  });
  if (extras.projectPt) {
    const [x, y] = px([extras.projectPt.lat, extras.projectPt.lon]);
    ctx.fillStyle = '#0A2540'; ctx.strokeStyle = '#FACC15'; ctx.lineWidth = 3;
    ctx.beginPath(); ctx.moveTo(x - 14, y - 22); ctx.lineTo(x - 4, y - 22); ctx.lineTo(x, y - 18); ctx.lineTo(x + 14, y - 18); ctx.lineTo(x + 14, y); ctx.lineTo(x - 14, y); ctx.closePath(); ctx.fill(); ctx.stroke();
    ctx.font = 'bold 13px Helvetica, Arial'; ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
    const w = ctx.measureText(extras.projectPt.name).width + 10;
    ctx.fillStyle = 'rgba(10,37,64,0.9)'; ctx.fillRect(x + 18, y - 20, w, 18);
    ctx.fillStyle = '#FACC15'; ctx.fillText(extras.projectPt.name, x + 23, y - 11);
  }
  (extras.reports || []).forEach((rp, i) => {
    const [x, y] = px([rp.lat, rp.lon]);
    ctx.fillStyle = '#ff6a13'; ctx.strokeStyle = '#0e1e30'; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(x, y, 9, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
    ctx.fillStyle = '#0e1e30'; ctx.font = 'bold 10px Helvetica, Arial'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(String(i + 1), x, y + 0.5);
  });
  (extras.camDirs || []).forEach((m) => {
    const [x, y] = px([m.lat, m.lon]);
    ctx.save(); ctx.translate(x, y);
    ctx.beginPath(); ctx.arc(0, 0, 7, 0, Math.PI * 2); ctx.fillStyle = m.kind === 'video' ? '#7c3aed' : '#ff6a13'; ctx.fill(); ctx.lineWidth = 2; ctx.strokeStyle = '#fff'; ctx.stroke();
    if (m.dir != null) {
      ctx.rotate((m.dir * Math.PI) / 180);
      ctx.beginPath(); ctx.moveTo(0, -24); ctx.lineTo(7, -9); ctx.lineTo(-7, -9); ctx.closePath();
      ctx.fillStyle = '#fff'; ctx.fill(); ctx.strokeStyle = '#0e1e30'; ctx.lineWidth = 1.5; ctx.stroke();
    }
    ctx.restore();
  });
  const main = extras.noOverlay ? null : mainPhotoOf(r);
  if (main) {
    const img = await loadImg(main.data, false);
    if (img) {
      const box = fitBox(img.width, img.height);
      const s = (r.overlayScale || 100) / 100;
      ctx.save();
      ctx.translate(W / 2, H / 2);
      ctx.rotate(((r.overlayAngle || 0) * Math.PI) / 180);
      ctx.scale(s, s);
      ctx.globalAlpha = (r.overlayOpacity || 75) / 100;
      ctx.drawImage(img, -box.w / 2, -box.h / 2, box.w, box.h);
      ctx.globalAlpha = 1;
      ctx.setLineDash([6, 4]);
      ctx.strokeStyle = '#ffd60a';
      ctx.lineWidth = 2 / s;
      ctx.strokeRect(-box.w / 2, -box.h / 2, box.w, box.h);
      ctx.restore();
    }
  }
  if (extras.heat?.length) drawHeat(ctx, extras.heat.map(([la, lo, w]) => [...px([la, lo]), w]), W, H);
  (extras.markers || []).forEach(({ pt, n: no, color }) => {
    const [x, y] = px(pt);
    ctx.beginPath(); ctx.arc(x, y, 13, 0, Math.PI * 2); ctx.fillStyle = color; ctx.fill();
    ctx.lineWidth = 3; ctx.strokeStyle = '#ffffff'; ctx.stroke();
    ctx.fillStyle = color === '#eab308' ? '#111' : '#fff'; ctx.font = 'bold 13px sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(String(no), x, y + 0.5);
  });
  if (extras.noCross) { try { return c.toDataURL('image/jpeg', 0.85); } catch { return null; } }
  ctx.strokeStyle = '#ff2d2d';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(W / 2 - 14, H / 2); ctx.lineTo(W / 2 + 14, H / 2);
  ctx.moveTo(W / 2, H / 2 - 14); ctx.lineTo(W / 2, H / 2 + 14);
  ctx.stroke();
  try { return c.toDataURL('image/jpeg', 0.85); } catch { return null; }
}

// simple heat: alpha blobs -> colour ramp (yellow > orange > red > dark red)
function drawHeat(ctx, pts, W, H, r = 34) {
  const sh = document.createElement('canvas');
  sh.width = W; sh.height = H;
  const s = sh.getContext('2d');
  pts.forEach(([x, y, w]) => {
    const g = s.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, `rgba(0,0,0,${Math.min(1, w / 2)})`); g.addColorStop(1, 'rgba(0,0,0,0)');
    s.fillStyle = g; s.fillRect(x - r, y - r, 2 * r, 2 * r);
  });
  const ramp = document.createElement('canvas');
  ramp.width = 256; ramp.height = 1;
  const rc = ramp.getContext('2d'), lg = rc.createLinearGradient(0, 0, 256, 0);
  [[0.2, '#fde047'], [0.5, '#f97316'], [0.8, '#dc2626'], [1, '#7f1d1d']].forEach(([o, c]) => lg.addColorStop(o, c));
  rc.fillStyle = lg; rc.fillRect(0, 0, 256, 1);
  const pal = rc.getImageData(0, 0, 256, 1).data, img = s.getImageData(0, 0, W, H), d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    const a = d[i + 3];
    if (!a) continue;
    const j = Math.min(255, a) * 4;
    d[i] = pal[j]; d[i + 1] = pal[j + 1]; d[i + 2] = pal[j + 2]; d[i + 3] = Math.min(230, 70 + a);
  }
  s.putImageData(img, 0, 0);
  ctx.drawImage(sh, 0, 0);
}

export function overviewView(pts, W = 720, H = 460) {
  const merc = ([la, lo]) => { const lr = (la * Math.PI) / 180; return [(lo + 180) / 360, (1 - Math.log(Math.tan(lr) + 1 / Math.cos(lr)) / Math.PI) / 2]; };
  const m = pts.map(merc);
  const xs = m.map((p) => p[0]), ys = m.map((p) => p[1]);
  const dx = Math.max(...xs) - Math.min(...xs) || 1e-9, dy = Math.max(...ys) - Math.min(...ys) || 1e-9;
  const z = Math.max(3, Math.min(19, Math.floor(Math.log2(Math.min((W - 90) / (dx * 256), (H - 90) / (dy * 256))))));
  const cx = (Math.max(...xs) + Math.min(...xs)) / 2, cy = (Math.max(...ys) + Math.min(...ys)) / 2;
  const lon = cx * 360 - 180;
  const lat = (Math.atan(Math.sinh(Math.PI * (1 - 2 * cy))) * 180) / Math.PI;
  return { lat, lon, zoom: z, photos: [] };
}

function dataToBytes(dataUrl) {
  const b64 = dataUrl.split(',')[1];
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return arr;
}

export async function exportKMZ(project, lang, asBlob) {
  const t = tl(lang);
  const zip = new JSZip();
  const files = zip.folder('files');
  let placemarks = '';
  let overlays = '';
  const memosHtml = (memos, base) => (memos || []).map((m, k) => {
    const fn = `${base}_memo${k + 1}.${extOf(m.mime, 'webm')}`;
    files.file(fn, m.blob);
    return `<p><a href="files/${fn}">${esc(t.pdfMemo)} ${fmtDur(m.duration)}</a>${m.transcript ? ': „' + esc(m.transcript) + '“' : ''}</p>`;
  }).join('');
  const mediaHtml = (p, base, label) => {
    if (p.kind === 'video') {
      const fn = `${base}.${extOf(p.mime, 'mp4')}`;
      files.file(fn, p.blob);
      if (p.thumb) files.file(`${base}_thumb.jpg`, dataToBytes(p.thumb));
      return `<p><b>${esc(label)}</b><br/><a href="files/${fn}">${p.thumb ? `<img src="files/${base}_thumb.jpg" width="400"/>` : esc(fn)}</a><br/>${esc(p.caption)}</p>${memosHtml(p.memos, base)}`;
    }
    files.file(`${base}.jpg`, dataToBytes(p.data));
    return `<p><b>${esc(label)}</b><br/><img src="files/${base}.jpg" width="400"/><br/>${esc(p.caption)}${p.dir != null ? `<br/>${esc(dirText(t, p))}` : ''}</p>${memosHtml(p.memos, base)}`;
  };
  let photoPms = '';
  project.reports.forEach((r, ri) => r.photos.forEach((p, pi) => {
    if (p.lat == null || p.lon == null) return;
    const base = `r${ri + 1}_p${pi + 1}`, img = p.kind === 'video' ? (p.thumb ? `${base}_thumb.jpg` : null) : `${base}.jpg`;
    const style = p.dir != null ? `<Style><IconStyle><heading>${norm(p.dir)}</heading><scale>1.1</scale><Icon><href>http://maps.google.com/mapfiles/kml/shapes/arrow.png</href></Icon></IconStyle></Style>` : '<styleUrl>#pin</styleUrl>';
    const ext = p.dir != null ? `<ExtendedData><Data name="Blickrichtung"><value>${norm(p.dir)}</value></Data><Data name="BlickrichtungQuelle"><value>${esc(p.dirSrc || 'manual')}</value></Data></ExtendedData>` : '';
    photoPms += `<Placemark><name>${esc(p.caption || `${r.title} ${pi + 1}`)}</name><description><![CDATA[${img ? `<img src="files/${img}" width="400"/><br/>` : ''}${esc(dirText(t, p))}]]></description>${style}${ext}<Point><coordinates>${p.lon},${p.lat},0</coordinates></Point></Placemark>\n`;
  }));
  project.reports.forEach((r, ri) => {
    let imgs = '';
    r.photos.forEach((p, pi) => {
      imgs += mediaHtml(p, `r${ri + 1}_p${pi + 1}`, p.kind === 'video' ? t.video : t[ROLE_KEYS[p.role] || 'roleGeneral']);
    });
    const desc = `<h3>${esc(r.title)}</h3><p>${esc(r.text || t.noText).replace(/\n/g, '<br/>')}</p><p>${esc(t.pdfCoords)} ${r.lat.toFixed(6)}, ${r.lon.toFixed(6)}</p>${memosHtml(r.memos, `r${ri + 1}`)}${imgs}`;
    placemarks += `<Placemark><name>${esc(r.title)}</name><description><![CDATA[${desc}]]></description><styleUrl>#pin</styleUrl><Point><coordinates>${r.lon},${r.lat},0</coordinates></Point></Placemark>\n`;
    const main = mainPhotoOf(r);
    if (main) {
      const box = fitBox(main.w, main.h);
      const s = (r.overlayScale || 100) / 100;
      const mpp = (156543.03392 * Math.cos((r.lat * Math.PI) / 180)) / 2 ** (r.zoom || 18);
      const halfWm = (box.w * s * mpp) / 2;
      const halfHm = (box.h * s * mpp) / 2;
      const dLat = halfHm / mPerLat(r.lat);
      const dLon = halfWm / (mPerLon(r.lat));
      const fn = `r${ri + 1}_p${r.photos.indexOf(main) + 1}.jpg`;
      const alpha = Math.round(((r.overlayOpacity || 75) / 100) * 255).toString(16).padStart(2, '0');
      overlays += `<GroundOverlay><name>${esc(r.title)} – ${esc(t.roleMain)}</name><color>${alpha}ffffff</color><Icon><href>files/${fn}</href></Icon><LatLonBox><north>${r.lat + dLat}</north><south>${r.lat - dLat}</south><east>${r.lon + dLon}</east><west>${r.lon - dLon}</west><rotation>${-(r.overlayAngle || 0)}</rotation></LatLonBox></GroundOverlay>\n`;
    }
  });
  let lines = '';
  let styles = '';
  const kcol = (c) => `ff${c.slice(5, 7)}${c.slice(3, 5)}${c.slice(1, 3)}`;
  Object.entries(PIPE_TYPES).forEach(([k, c]) => {
    styles += `<Style id="pipe_${k}"><LineStyle><color>${kcol(c)}</color><width>4</width></LineStyle></Style>\n`;
  });
  (project.pipes || []).forEach((pp) => {
    const pts = pipeLatLngs(pp, project.fittings || []);
    if (pts.length < 2) return;
    const desc = `${esc(t['type_' + pp.type])}${pp.dn ? ' · ' + esc(pp.dn) : ''}<br/>${esc(t.pipeLength)}: ${pathLength(pts).toFixed(1)} m`;
    lines += `<Placemark><name>${esc(pp.name)}</name><description><![CDATA[${desc}]]></description><styleUrl>#pipe_${PIPE_TYPES[pp.type] ? pp.type : 'other'}</styleUrl><LineString><tessellate>1</tessellate><coordinates>${pts.map(([la, lo]) => `${lo},${la},0`).join(' ')}</coordinates></LineString></Placemark>\n`;
  });
  let fits = '';
  Object.entries(FITTING_TYPES).forEach(([k, c]) => {
    styles += `<Style id="fit_${k}"><IconStyle><color>${kcol(c)}</color><scale>0.9</scale><Icon><href>http://maps.google.com/mapfiles/kml/shapes/placemark_circle.png</href></Icon></IconStyle></Style>\n`;
  });
  const repHtml = {};
  repairEntries(project).forEach(({ o, m, files: rf }) => {
    rf.forEach((x) => files.file(x.name, dataToBytes(x.data)));
    const imgs = rf.map((x) => `<td><b>${esc(t[x.which === 'before' ? 'mtBefore' : 'mtAfter'])}</b><br/><a href="files/${x.name}"><img src="files/${x.name}" width="220"/></a></td>`).join('');
    repHtml[o.id] = (repHtml[o.id] || '') + `<p>${esc(fill(t.mtRepairedLine, { by: m.repair.by, date: new Date(m.repair.date).toLocaleDateString(t.locale), prev: t['mt_' + m.repair.prev] }))}${m.repair.note ? ': ' + esc(m.repair.note) : ''}</p>${imgs ? `<table><tr>${imgs}</tr></table>` : ''}`;
  });
  (project.fittings || []).forEach((f, fi) => {
    const media = (f.media || []).map((m, k) => mediaHtml(m, `f${fi + 1}_m${k + 1}`, m.kind === 'video' ? t.video : t.btnPhoto)).join('') + (repHtml[f.id] || '');
    const st = f.state && f.state !== 'none' ? `<br/>${esc(t.fitState)}: <b>${esc(t['st_' + f.state])}</b>` : '';
    fits += `<Placemark><name>${esc(f.name)}</name><description><![CDATA[${esc(t['fit_' + f.type])}${st}${f.note ? '<br/>' + esc(f.note) : ''}${memosHtml(f.memos, `f${fi + 1}`)}${media}]]></description><styleUrl>#fit_${FITTING_TYPES[f.type] ? f.type : 'other'}</styleUrl><Point><coordinates>${f.lon},${f.lat},0</coordinates></Point></Placemark>\n`;
  });
  let pitsKml = '';
  (project.pits || []).forEach((p) => {
    const c = computePit(p);
    const rows = [...c.rows, c.excav].map((r) => `<tr><td>${esc(layerName(t, r))}</td><td>${f2(r.m3, t.locale)} m³</td><td>${f2(r.t, t.locale)} t</td><td>${r.cost != null ? esc(eur(r.cost, t.locale)) : '–'}</td></tr>`).join('');
    const desc = `${esc(t.pitArea)}: ${f2(c.area, t.locale)} · ${esc(t.pitDepth)}: ${f2(c.depth, t.locale)} · DN ${esc(p.dn || '–')}<table border="1" cellpadding="3">${rows}</table><b>${esc(t.pitTotal)}: ${esc(eur(c.total, t.locale))}</b>${p.note ? '<br/>' + esc(p.note) : ''}`;
    const ring = [...pitRing(p), pitRing(p)[0]].map(([la, lo]) => `${lo},${la},0`).join(' ');
    pitsKml += `<Placemark><name>${esc(p.name)}</name><description><![CDATA[${desc}${repHtml[p.id] || ''}]]></description><styleUrl>#pit</styleUrl><Polygon><tessellate>1</tessellate><outerBoundaryIs><LinearRing><coordinates>${ring}</coordinates></LinearRing></outerBoundaryIs></Polygon></Placemark>\n`;
  });
  styles += '<Style id="pit"><LineStyle><color>ff0c41c2</color><width>2.5</width></LineStyle><PolyStyle><color>6616 73f9</color></PolyStyle></Style>\n'.replace('6616 73f9', '661673f9');
  const kml = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2"><Document><name>${esc(project.title)}</name>
<Style id="pin"><IconStyle><color>ff136aff</color><scale>1.2</scale><Icon><href>http://maps.google.com/mapfiles/kml/paddle/orange-circle.png</href></Icon></IconStyle></Style>
${styles}<Folder><name>${esc(t.pipesTitle)}</name>
${lines}</Folder>
<Folder><name>${esc(t.fittingsTitle)}</name>
${fits}</Folder>
<Folder><name>${esc(t.pitSection)}</name>
${pitsKml}</Folder>
<Folder><name>${esc(t.reports)}</name>
${placemarks}</Folder>
<Folder><name>${esc(t.roleMain)}</name>
${overlays}</Folder>
<Folder><name>${esc(t.phFolder)}</name>
${photoPms}</Folder>
</Document></kml>`;
  zip.file('doc.kml', kml);
  const blob = await zip.generateAsync({ type: 'blob', mimeType: 'application/vnd.google-earth.kmz' });
  if (asBlob) return blob;
  download(blob, `${projectStem(project)}.kmz`);
}

export async function exportJSON(project, asBlob) {
  const ag = getAgents();
  const blob = new Blob([JSON.stringify(await serialize(ag.length ? { ...project, agentDb: ag } : project))], { type: 'application/json' });
  localStorage.setItem(`giss_bak_${project.id}`, String(Date.now()));
  if (asBlob) return blob;
  download(blob, `${projectStem(project)}${APP_INFO.backupExt}`);
}
